import React, { useState, useRef } from 'react';
import { MapPin, ArrowRight, MessageCircle, Play, Pause, Clock, Award, Heart, Users } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';

const PROMO_EXPIRY = new Date('2026-06-01T23:59:59');

const MARATHON_IMAGES = [
  'https://images.unsplash.com/photo-1667781838690-5f32ea0ccea6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxtYXJhdGhvbiUyMHJ1bm5lcnMlMjBmaW5pc2hpbmclMjByYWNlJTIwbGVncyUyMHRpcmVkJTIwcmVjb3Zlcnl8ZW58MHx8fHwxNzc3ODk3NTY0fDA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1749131871347-6d211039ac3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjBtYXNzYWdlJTIwbGVnJTIwcmVjb3ZlcnklMjBhdGhsZXRlJTIwdHJlYXRtZW50JTIwZGVlcCUyMHRpc3N1ZXxlbnwwfHx8fDE3Nzc4OTc1NzN8MA&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1617952986600-802f965dcdbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxzcG9ydHMlMjBtYXNzYWdlJTIwbGVnJTIwcmVjb3ZlcnklMjBhdGhsZXRlJTIwdHJlYXRtZW50JTIwZGVlcCUyMHRpc3N1ZXxlbnwwfHx8fDE3Nzc4OTc1NzN8MA&ixlib=rb-4.1.0&q=85',
];

const VIDEO_URL = '/lunchtime-massage-v5.mp4';
const VIDEO_POSTER = 'https://images.unsplash.com/photo-1749131871347-6d211039ac3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxzcG9ydHMlMjBtYXNzYWdlJTIwbGVnJTIwcmVjb3ZlcnklMjBhdGhsZXRlJTIwdHJlYXRtZW50JTIwZGVlcCUyMHRpc3N1ZXxlbnwwfHx8fDE3Nzc4OTc1NzN8MA&ixlib=rb-4.1.0&q=85';

const SHARE_MSG = `Just ran the Dublin Half Marathon? Your legs DESERVE this!\n\nGet €10 OFF Sports Massage, Deep Tissue & Leg Recovery at Dublin Holistic Massage!\n\nHow to claim:\n1. WhatsApp us a photo of your medal\n2. Book your recovery massage\n3. Feel amazing again!\n\nValid for runners, friends, family & supporters!\nOnly at 19 Talbot Street, Dublin 1\nValid this week only!\n\nWhatsApp: +353 85 774 7474\nBook: www.dublinholisticmassages.com`;

const DublinMarathonPromo = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  if (new Date() > PROMO_EXPIRY) return null;

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <>
      <section
        className="py-20 relative overflow-hidden"
        data-testid="dublin-marathon-promo"
        style={{ background: 'linear-gradient(135deg, #0F2922 0%, #16382E 40%, #1C473A 70%, #0F2922 100%)' }}
      >
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-5 border border-orange-500/30">
              <Award size={14} />
              Bank Holiday Special
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-3 leading-tight">
              Dublin Half Marathon
              <span className="block text-orange-400 italic mt-2 text-2xl sm:text-3xl lg:text-4xl">
                Recovery Massage — €10 Off!
              </span>
            </h2>

            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Congratulations on finishing! Your legs carried you 21km yesterday — now let us take care of them.
              <strong className="text-white"> Sports Massage, Deep Tissue & Leg Recovery</strong> at 19 Talbot Street.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-start">

            {/* Left — Video + Gallery */}
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-orange-500/30 group cursor-pointer" onClick={toggleVideo}>
                <video
                  ref={videoRef}
                  poster={VIDEO_POSTER}
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-[300px] sm:h-[360px] object-cover"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  data-testid="marathon-promo-video"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
                <div className={`absolute inset-0 bg-black/30 flex items-center justify-center transition-opacity ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}>
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 hover:scale-110 transition-transform">
                    {isPlaying ? <Pause size={28} className="text-white" /> : <Play size={28} className="text-white ml-1" />}
                  </div>
                </div>
                <div className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-bold uppercase px-3 py-1 rounded-full">
                  Limited Time
                </div>
              </div>

              {/* Image Gallery */}
              <div className="grid grid-cols-3 gap-3">
                {MARATHON_IMAGES.map((img, i) => (
                  <div key={img} className="rounded-xl overflow-hidden border border-white/10 hover:border-orange-500/40 transition-colors">
                    <img src={img} alt={`Marathon recovery ${i + 1}`} className="w-full h-24 sm:h-28 object-cover" loading="lazy" />
                  </div>
                ))}
              </div>

              {/* Share */}
              <Button
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_MSG)}`, '_blank')}
                className="w-full bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-5 text-sm font-medium"
                data-testid="marathon-whatsapp-share"
              >
                <MessageCircle size={18} className="mr-2" />
                Share with Running Friends
              </Button>
            </div>

            {/* Right — Deal Info */}
            <div className="space-y-5">

              {/* Big Savings Card */}
              <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/5 rounded-3xl p-6 sm:p-8 border border-orange-500/40">
                <div className="text-center mb-5">
                  <div className="text-orange-400 text-6xl font-display font-bold mb-1">€10 OFF</div>
                  <p className="text-white text-lg font-semibold">Recovery Massage</p>
                  <p className="text-white/50 text-sm">Valid for a limited time</p>
                </div>

                {/* How to Claim */}
                <div className="bg-white/10 rounded-xl p-5 mb-5">
                  <p className="text-orange-400 font-semibold text-sm mb-3 flex items-center gap-2">
                    <MessageCircle size={16} />
                    How to Claim Your €10 Off
                  </p>
                  <ol className="space-y-2.5 text-white/90 text-sm list-decimal list-inside">
                    <li>WhatsApp us a <strong className="text-white">photo of your medal</strong></li>
                    <li>We'll confirm your <strong className="text-white">€10 discount</strong></li>
                    <li>Book your recovery session</li>
                    <li>Walk out feeling brand new!</li>
                  </ol>
                </div>

                {/* Who qualifies */}
                <div className="bg-white/5 rounded-xl p-4 mb-5 border border-white/10">
                  <p className="text-white font-medium text-sm mb-2 flex items-center gap-2">
                    <Users size={16} className="text-orange-400" />
                    Who Qualifies?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full text-xs border border-orange-500/30">Runners</span>
                    <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full text-xs border border-orange-500/30">Friends</span>
                    <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full text-xs border border-orange-500/30">Family</span>
                    <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full text-xs border border-orange-500/30">Supporters</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <Button
                  onClick={() => window.open('https://wa.me/353857747474?text=Hi! I ran the Dublin Half Marathon and I\'d like to claim my €10 off recovery massage. Here\'s my medal photo:', '_blank')}
                  className="w-full bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-6 text-sm font-bold uppercase tracking-wider group mb-3"
                  data-testid="marathon-whatsapp-claim"
                >
                  <MessageCircle size={18} className="mr-2" />
                  WhatsApp Your Medal Photo
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                <Button
                  onClick={() => setBookingOpen(true)}
                  className="w-full bg-orange-500 hover:bg-orange-400 text-white rounded-full py-5 text-sm font-medium group"
                  data-testid="marathon-book-btn"
                >
                  Book Recovery Massage
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              {/* Location & Services */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
                  <MapPin size={18} className="text-orange-400 mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">19 Talbot Street</p>
                  <p className="text-white/50 text-[10px]">Dublin 1 — City Centre only</p>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
                  <Clock size={18} className="text-orange-400 mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">This Week Only</p>
                  <p className="text-white/50 text-[10px]">Limited time offer</p>
                </div>
              </div>

              {/* Treatments */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <p className="text-white/50 text-[10px] uppercase tracking-wider mb-3 font-semibold">Recommended Treatments</p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-white/10 text-white/90 px-3 py-1.5 rounded-full text-xs border border-white/15">Sports Massage</span>
                  <span className="bg-white/10 text-white/90 px-3 py-1.5 rounded-full text-xs border border-white/15">Deep Tissue</span>
                  <span className="bg-white/10 text-white/90 px-3 py-1.5 rounded-full text-xs border border-white/15">Leg Recovery</span>
                  <span className="bg-white/10 text-white/90 px-3 py-1.5 rounded-full text-xs border border-white/15">Full Body</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
};

export default DublinMarathonPromo;
